import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  Users,
  MessageSquareText,
  Activity as ActivityIcon,
  FileText,
  Search as SearchIcon,
} from "lucide-react";

import api from "@/lib/api";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";

const KIND_META = {
  company: { label: "Firmen", icon: Building2, navigate: (h) => `/companies/${h.company_id || h.id}` },
  contact: { label: "Personen", icon: Users, navigate: (h) => (h.company_id ? `/companies/${h.company_id}` : `/contacts`) },
  note: { label: "Gespräche", icon: MessageSquareText, navigate: (h) => (h.company_id ? `/companies/${h.company_id}` : `/notes`) },
  activity: { label: "Aktivitäten", icon: ActivityIcon, navigate: (h) => (h.company_id ? `/companies/${h.company_id}` : `/activities`) },
  quote: { label: "Angebote", icon: FileText, navigate: (h) => (h.company_id ? `/companies/${h.company_id}` : `/quotes`) },
};

function useDebouncedValue(value, delay = 200) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/**
 * Globale Suche – Command Palette.
 * Props:
 *  - open: boolean
 *  - onOpenChange: (next) => void
 */
export default function GlobalSearch({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query, 200);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const { data, isFetching } = useQuery({
    queryKey: ["search", debounced],
    queryFn: async () => (await api.get(`/search`, { params: { q: debounced } })).data,
    enabled: open && debounced.trim().length > 0,
    staleTime: 30_000,
  });

  const groups = useMemo(() => {
    if (!data) return [];
    return [
      { kind: "company", items: data.companies },
      { kind: "contact", items: data.contacts },
      { kind: "note", items: data.notes },
      { kind: "activity", items: data.activities },
      { kind: "quote", items: data.quotes },
    ].filter((g) => g.items && g.items.length > 0);
  }, [data]);

  function handleSelect(hit) {
    const meta = KIND_META[hit.kind];
    onOpenChange?.(false);
    if (meta) navigate(meta.navigate(hit));
  }

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} data-testid="global-search-dialog">
      <CommandInput
        ref={inputRef}
        placeholder="Firmen, Personen, Gespräche, Aktivitäten oder Angebote suchen…"
        value={query}
        onValueChange={setQuery}
        data-testid="global-search-input"
      />
      <CommandList data-testid="global-search-list">
        {query.trim() === "" ? (
          <div className="py-10 text-center text-sm text-gray-400" data-testid="global-search-hint">
            Beginne zu tippen, um Firmen, Personen, Gespräche, Aktivitäten und Angebote zu durchsuchen.
          </div>
        ) : groups.length === 0 && !isFetching ? (
          <CommandEmpty data-testid="global-search-empty">Keine Treffer für „{query}“.</CommandEmpty>
        ) : null}
        {groups.map((g) => {
          const meta = KIND_META[g.kind];
          const Icon = meta.icon;
          return (
            <CommandGroup key={g.kind} heading={meta.label} data-testid={`global-search-group-${g.kind}`}>
              {g.items.map((hit) => (
                <CommandItem
                  key={`${g.kind}-${hit.id}`}
                  value={`${g.kind}-${hit.id}-${hit.title}`}
                  onSelect={() => handleSelect(hit)}
                  data-testid={`global-search-item-${g.kind}-${hit.id}`}
                  className="cursor-pointer"
                >
                  <Icon className="w-4 h-4 text-gray-500" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">{hit.title}</div>
                    {hit.subtitle ? (
                      <div className="text-xs text-gray-500 truncate">{hit.subtitle}</div>
                    ) : null}
                  </div>
                  {hit.meta ? (
                    <span className="text-[10px] uppercase tracking-wider text-gray-400">{hit.meta}</span>
                  ) : null}
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
      <div className="border-t border-gray-100 px-3 py-2 flex items-center justify-between text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <SearchIcon className="w-3 h-3" />
          {isFetching ? "Suche läuft…" : data ? `${data.total} Treffer` : "Tippe zum Suchen"}
        </span>
        <CommandShortcut>Esc zum Schließen</CommandShortcut>
      </div>
    </CommandDialog>
  );
}
